/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import { Provider, defaultTheme } from '@adobe/react-spectrum';

import ProductPicker from '../ProductPicker/ProductPicker';
import ProductPickerContextProvider from '../ProductPicker/ProductPickerContext';
import ProductField from '../ProductField/ProductField';
import SelectionContextProvider from '../common/SelectionContext';
import withRoot from '../Root/withRoot';
import useLocale from '../common/useLocale';
import { useConfigContext } from '../ConfigContext/ConfigContext';

const DEFAULT_TREE_NAVIGATION_CATEGORY_LIMIT = 20;
const MAX_TREE_NAVIGATION_CATEGORY_LIMIT = 100;
const WEB_COMPONENT_TAG = 'product-field';

const handleTreeNavigationCategoryLimitValue = treeNavigationCategoryLimit => {
    if (!treeNavigationCategoryLimit) return DEFAULT_TREE_NAVIGATION_CATEGORY_LIMIT;
    if (treeNavigationCategoryLimit > MAX_TREE_NAVIGATION_CATEGORY_LIMIT) return MAX_TREE_NAVIGATION_CATEGORY_LIMIT;
    return treeNavigationCategoryLimit;
};

const ProductFieldRoot = props => {
    const {
        multiple,
        value,
        currentValue,
        selectionId,
        pickerSelectedData,
        target,
        targetType,
        enablePreviewDateFilter,
        provideWebComponentApi
    } = props;
    const [[config]] = useConfigContext();
    const treeNavigationCategoryLimit = handleTreeNavigationCategoryLimitValue(
        Number(config.treeNavigationCategoryLimit)
    );
    const treeNavigationPagination = {
        total: 0,
        page: 1,
        limit: treeNavigationCategoryLimit
    };

    const domRoot = useRef(null);
    const locale = useLocale();

    return (
        <div ref={domRoot}>
            <Provider theme={defaultTheme} colorScheme="light" locale={locale}>
                <ProductPickerContextProvider
                    selectionId={selectionId}
                    treeNavigationPagination={treeNavigationPagination}
                    domRoot={domRoot}
                    webComponentTag={WEB_COMPONENT_TAG}
                >
                    <SelectionContextProvider
                        multiple={multiple}
                        preSelection={value}
                        currentValue={currentValue}
                        preSelectionKey={selectionId}
                        webComponentTag={WEB_COMPONENT_TAG}
                        target={target}
                        pickerSelectedData={pickerSelectedData}
                        targetType={targetType}
                        domRoot={domRoot}
                        provideWebComponentApi={provideWebComponentApi}
                    >
                        <ProductField {...props}>
                            <ProductPicker
                                multiple={multiple}
                                selectionId={selectionId}
                                enablePreviewDateFilter={enablePreviewDateFilter}
                            />
                        </ProductField>
                    </SelectionContextProvider>
                </ProductPickerContextProvider>
            </Provider>
        </div>
    );
};

ProductFieldRoot.propTypes = {
    enablePreviewDateFilter: PropTypes.bool,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    labelledBy: PropTypes.string,
    inputClasses: PropTypes.string,
    value: PropTypes.string, // If multiple is true, this should be a comma separated list
    name: PropTypes.string,
    multiple: PropTypes.bool,
    selectionId: PropTypes.oneOf(['id', 'uid', 'sku', 'slug', 'combinedSku', 'combinedSlug']).isRequired,
    target: PropTypes.string,
    targetType: PropTypes.string,
    showLink: PropTypes.bool,
    provideWebComponentApi: PropTypes.func,
    currentValue: PropTypes.string,
    pickerSelectedData: PropTypes.arrayOf(PropTypes.object)
};

export default withRoot(ProductFieldRoot);
